<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { Camera, Fingerprint, KeyRound, Loader2, LogOut, ShieldAlert, Trash2, User as UserIcon } from 'lucide-vue-next'
import Card from '@/components/ui/Card.vue'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import Modal from '@/components/ui/Modal.vue'
import StatusDot from '@/components/ui/StatusDot.vue'
import Badge from '@/components/ui/Badge.vue'
import AvatarCropper from '@/components/settings/AvatarCropper.vue'
import {
  avatarUrl,
  changePassword,
  getAuthStatus,
  logout as apiLogout,
  removeAvatar,
  setupAuth,
  updateProfile,
  uploadAvatar,
} from '@/api/auth'
import {
  createPasskey,
  passkeyBeginRegistration,
  passkeyDelete,
  passkeyFinishRegistration,
  passkeyList,
  probePasskeySupport,
  type PasskeyInfo,
  type PasskeySupport,
} from '@/api/passkey'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const auth = useAuthStore()
const toast = useToast()
const router = useRouter()
const qc = useQueryClient()

const statusQuery = useQuery({
  queryKey: ['auth', 'status'],
  queryFn: () => getAuthStatus(),
})

const passkeySupport = ref<PasskeySupport>({ available: false })
const passkeyLabel = ref('')
const registering = ref(false)

onMounted(async () => {
  passkeySupport.value = await probePasskeySupport()
})

const passkeyQuery = useQuery({
  queryKey: ['auth', 'passkeys'],
  queryFn: () => passkeyList(),
  enabled: computed(() => statusQuery.data.value?.configured === true),
})

const showChangePassword = ref(false)
const showSetup = ref(false)
const showLogoutConfirm = ref(false)
const showRegisterPasskey = ref(false)

const cpForm = reactive({
  old: '',
  new: '',
  confirm: '',
})

const setupForm = reactive({
  username: '',
  password: '',
  confirm: '',
})

// v3.13.92: 昵称 + 头像 (上传/移除)。
const nickname = ref(auth.user?.nickname ?? '')
watch(() => auth.user?.nickname, (v) => { if (v !== undefined) nickname.value = v ?? '' })
const nicknameSaving = ref(false)
async function saveNickname(): Promise<void> {
  if (nicknameSaving.value) return
  nicknameSaving.value = true
  try {
    const res = await updateProfile(nickname.value.trim())
    if (res?.user) auth.setAuth(res.user, auth.getToken() ?? '')
    void qc.invalidateQueries({ queryKey: ['auth', 'status'] })
    toast.success(t('settings.auth.nicknameSaved'), t('settings.auth.nickname'))
  } catch {
    /* client 统一弹错 */
  } finally {
    nicknameSaving.value = false
  }
}

const avatarInput = ref<HTMLInputElement | null>(null)
const avatarSaving = ref(false)
// 待裁剪的原图 (非空 = 裁剪器打开中)
const pendingAvatar = ref<File | null>(null)
const currentAvatar = computed(() => avatarUrl(auth.user?.avatarV))
function pickAvatar(): void {
  avatarInput.value?.click()
}
function onAvatarPicked(e: Event): void {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = '' // 允许重复选同一文件
  if (!file || avatarSaving.value) return
  if (file.size > 20 * 1024 * 1024) {
    toast.error(t('settings.auth.avatarTooLarge'), t('settings.auth.avatar'))
    return
  }
  if (!/^image\/(png|jpeg|webp)$/i.test(file.type)) {
    toast.error(t('settings.auth.avatarInvalid'), t('settings.auth.avatar'))
    return
  }
  // 进入裁剪器, 确认后再上传 (实际提交的是裁剪后的小图)
  pendingAvatar.value = file
}
async function onCropConfirm(blob: Blob): Promise<void> {
  pendingAvatar.value = null
  if (avatarSaving.value) return
  avatarSaving.value = true
  try {
    const file = new File([blob], 'avatar.png', { type: 'image/png' })
    const res = await uploadAvatar(file)
    if (auth.user) auth.setAuth({ ...auth.user, avatarV: res.avatarV }, auth.getToken() ?? '')
    toast.success(t('settings.auth.avatarSaved'), t('settings.auth.avatar'))
  } catch {
    /* client 统一弹错 */
  } finally {
    avatarSaving.value = false
  }
}
async function onAvatarRemove(): Promise<void> {
  if (avatarSaving.value) return
  avatarSaving.value = true
  try {
    await removeAvatar()
    if (auth.user) auth.setAuth({ ...auth.user, avatarV: 0 }, auth.getToken() ?? '')
    toast.success(t('settings.auth.avatarRemoved'), t('settings.auth.avatar'))
  } catch {
    /* client 统一弹错 */
  } finally {
    avatarSaving.value = false
  }
}

const cpMutation = useMutation({
  mutationFn: () =>
    changePassword({
      old: cpForm.old,
      new: cpForm.new,
      new_confirm: cpForm.confirm,
    }),
  onSuccess: () => {
    toast.success(t('settings.auth.passwordChanged'), t('settings.auth.changePassword'))
    cpForm.old = ''
    cpForm.new = ''
    cpForm.confirm = ''
    showChangePassword.value = false
    void qc.invalidateQueries({ queryKey: ['auth', 'status'] })
  },
})

const setupMutation = useMutation({
  mutationFn: () =>
    setupAuth({
      username: setupForm.username.trim(),
      password: setupForm.password,
    }),
  onSuccess: (resp) => {
    toast.success(t('settings.auth.setupSuccess', { username: resp.username }))
    setupForm.username = ''
    setupForm.password = ''
    setupForm.confirm = ''
    showSetup.value = false
    void qc.invalidateQueries({ queryKey: ['auth', 'status'] })
  },
})

// v3.13.39: 通行密钥注册流程 — begin → navigator.credentials.create → finish.
async function registerPasskey(): Promise<void> {
  if (!passkeySupport.value.available) {
    toast.error(t('settings.auth.passkeyUnsupported'))
    return
  }
  if (!passkeyLabel.value.trim()) {
    passkeyLabel.value = 'Admin passkey'
  }
  registering.value = true
  try {
    const begin = await passkeyBeginRegistration(passkeyLabel.value.trim())
    const created = await createPasskey(begin)
    const finish = await passkeyFinishRegistration(created)
    toast.success(t('settings.auth.passkeyRegistered', { id: finish.id.slice(0, 8) }))
    showRegisterPasskey.value = false
    passkeyLabel.value = ''
    void qc.invalidateQueries({ queryKey: ['auth', 'passkeys'] })
  } catch (err) {
    const e = err as Error
    // v3.13.39: 详细诊断输出 - 浏览器 F12 console 直接可读,
    // toast 也带上原始类型名方便截屏反馈.
    // eslint-disable-next-line no-console
    console.error('[passkey register]', {
      name: e?.name,
      message: e?.message,
      code: (e as { code?: string }).code,
      stack: e?.stack?.split('\n').slice(0, 5).join(' | '),
    })
    if (e.name === 'NotAllowedError') return // 用户取消
    toast.error((e?.name ? `[${e.name}] ` : '') + (e?.message || t('settings.auth.passkeyFailed')))
  } finally {
    registering.value = false
  }
}

function deletePasskey(pk: PasskeyInfo): void {
  if (!confirm(t('settings.auth.passkeyConfirmDelete', { label: pk.label }))) return
  deleteMutation.mutate(pk.id)
}

const deleteMutation = useMutation({
  mutationFn: (id: string) => passkeyDelete(id),
  onSuccess: () => {
    toast.success(t('settings.auth.passkeyDeleted'))
    void qc.invalidateQueries({ queryKey: ['auth', 'passkeys'] })
  },
  onError: (err: Error) => toast.error(err.message || t('settings.auth.passkeyFailed')),
})

async function doLogout(): Promise<void> {
  try {
    await apiLogout()
  } catch {
    /* ignore — local clear still proceeds */
  }
  auth.clearAuth()
  showLogoutConfirm.value = false
  toast.info(t('settings.auth.loggedOut'))
  router.push({ name: 'login' }).catch(() => {
    /* ignore */
  })
}

function openChangePassword(): void {
  cpForm.old = ''
  cpForm.new = ''
  cpForm.confirm = ''
  showChangePassword.value = true
}

function openSetup(): void {
  setupForm.username = 'admin'
  setupForm.password = ''
  setupForm.confirm = ''
  showSetup.value = true
}

function openRegisterPasskey(): void {
  passkeyLabel.value = 'Admin passkey'
  showRegisterPasskey.value = true
}

const isSetupNeeded = computed(
  () => statusQuery.data.value?.configured === false,
)
const isDefaultUser = computed(() => statusQuery.data.value?.is_default === true)
const username = computed(() => statusQuery.data.value?.username ?? auth.user?.username ?? '')
const loggedIn = computed(
  () => statusQuery.data.value?.logged_in === true || auth.isAuthenticated,
)
const passkeys = computed<PasskeyInfo[]>(
  () => passkeyQuery.data.value?.keys ?? [],
)

const cpMismatch = computed(
  () => cpForm.new.length > 0 && cpForm.new !== cpForm.confirm,
)
const cpTooShort = computed(() => cpForm.new.length > 0 && cpForm.new.length < 6)
const cpCanSubmit = computed(
  () =>
    !cpMismatch.value &&
    !cpTooShort.value &&
    cpForm.old.length > 0 &&
    cpForm.new.length >= 6,
)

const setupMismatch = computed(
  () =>
    setupForm.password.length > 0 && setupForm.password !== setupForm.confirm,
)
const setupTooShort = computed(
  () => setupForm.password.length > 0 && setupForm.password.length < 6,
)
const setupCanSubmit = computed(
  () =>
    setupForm.username.trim().length >= 3 &&
    setupForm.password.length >= 6 &&
    !setupMismatch.value,
)

function submitPassword(): void {
  if (!cpCanSubmit.value) return
  cpMutation.mutate()
}

function submitSetup(): void {
  if (!setupCanSubmit.value) return
  setupMutation.mutate()
}
</script>

<template>
  <div class="space-y-6">
    <!-- Initial setup banner — only when admin account not configured -->
    <Card v-if="isSetupNeeded">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-start gap-3">
          <div class="rounded-md bg-warning/15 p-2 text-warning">
            <ShieldAlert :size="20" />
          </div>
          <div>
            <h3 class="text-sm font-semibold text-fg">{{ t('settings.auth.setupRequired') }}</h3>
            <p class="mt-1 text-xs text-fg-muted">{{ t('settings.auth.setupHint') }}</p>
          </div>
        </div>
        <Button variant="primary" size="sm" @click="openSetup">
          {{ t('settings.auth.setupAction') }}
        </Button>
      </div>
    </Card>

    <!-- Account info card -->
    <Card :title="t('settings.auth.account')" :subtitle="t('settings.auth.subtitle')">
      <div class="space-y-4">
        <!-- v3.13.92: 头像 + 昵称 -->
        <div class="flex flex-wrap items-center gap-4 rounded-md border border-border bg-bg-elevated px-4 py-3">
          <img
            v-if="currentAvatar"
            :src="currentAvatar"
            :alt="auth.user?.nickname || auth.user?.username || 'avatar'"
            class="h-12 w-12 shrink-0 rounded-full object-cover"
          />
          <div
            v-else
            class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-500/15 text-sm font-semibold text-brand-600 dark:text-brand-300"
          >
            {{ (auth.user?.nickname || auth.user?.username || '?').slice(0, 2).toUpperCase() }}
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-semibold text-fg truncate">{{ auth.user?.nickname || username || '—' }}</span>
              <Badge v-if="isDefaultUser" variant="warning">{{ t('settings.auth.defaultAccount') }}</Badge>
            </div>
            <div class="flex items-center gap-2 mt-0.5">
              <StatusDot :status="loggedIn ? 'online' : 'offline'" :pulse="loggedIn" />
              <span class="text-xs text-fg-muted">
                {{ loggedIn ? t('settings.auth.loggedIn') : t('settings.auth.notLoggedIn') }}
              </span>
            </div>
          </div>
          <div class="flex flex-col items-end gap-2">
            <input
              ref="avatarInput"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              class="hidden"
              @change="onAvatarPicked"
            />
            <div class="flex gap-2">
              <Button variant="outline" size="sm" :disabled="!loggedIn || avatarSaving" @click="pickAvatar">
                <template #icon><Camera :size="14" /></template>
                {{ t('settings.auth.uploadAvatar') }}
              </Button>
              <Button
                v-if="currentAvatar"
                variant="ghost"
                size="sm"
                :disabled="!loggedIn || avatarSaving"
                @click="onAvatarRemove"
              >
                {{ t('settings.auth.removeAvatar') }}
              </Button>
            </div>
          </div>
        </div>

        <!-- 昵称编辑 -->
        <div class="flex flex-wrap items-end gap-2">
          <div class="min-w-[200px] flex-1">
            <label class="mb-1 block text-xs text-fg-muted">{{ t('settings.auth.nickname') }}</label>
            <Input
              v-model="nickname"
              :placeholder="t('settings.auth.nicknamePlaceholder')"
              maxlength="32"
              @keyup.enter="saveNickname"
            />
          </div>
          <Button variant="primary" size="md" :disabled="!loggedIn || nicknameSaving" @click="saveNickname">
            {{ t('common.save') }}
          </Button>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <Button variant="primary" size="md" :disabled="isSetupNeeded" @click="openChangePassword">
            <template #icon><KeyRound :size="14" /></template>
            {{ t('settings.auth.changePassword') }}
          </Button>
          <Button variant="outline" size="md" :disabled="!loggedIn" @click="showLogoutConfirm = true">
            <template #icon><LogOut :size="14" /></template>
            {{ t('auth.logout') }}
          </Button>
        </div>
      </div>
    </Card>

    <!-- Passkeys (v3.13.39: 硬件密钥管理) -->
    <Card
      v-if="!isSetupNeeded"
      :title="t('settings.auth.passkeysTitle')"
      :subtitle="t('settings.auth.passkeysSubtitle')"
    >
      <div v-if="!passkeySupport.available" class="rounded-md border border-warning/30 bg-warning/10 p-3 text-xs text-warning">
        <Fingerprint :size="12" class="-mt-0.5 mr-1 inline align-middle" />
        {{ t('auth.passkeyUnsupported') }}
        <span v-if="passkeySupport.reason" class="block pl-5 pt-1 text-xs text-fg-subtle">
          {{ passkeySupport.reason }}
        </span>
      </div>
      <template v-else>
        <div class="space-y-2">
          <div v-if="passkeys.length === 0" class="rounded-md border border-dashed border-border px-4 py-6 text-center text-xs text-fg-muted">
            {{ t('settings.auth.passkeysEmpty') }}
          </div>
          <div
            v-for="pk in passkeys"
            :key="pk.id"
            class="flex items-center gap-3 rounded-md border border-border bg-bg-elevated/40 px-3 py-2"
          >
            <div class="flex h-8 w-8 items-center justify-center rounded-md bg-success/15 text-success">
              <Fingerprint :size="14" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="truncate text-sm font-medium text-fg">{{ pk.label }}</div>
              <div class="font-mono text-xs text-fg-subtle">
                {{ pk.id.slice(0, 16) }}… · {{ t('settings.auth.passkeySignCount', { n: pk.sign_count }) }}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              :loading="deleteMutation.isPending.value && deleteMutation.variables.value === pk.id"
              @click="deletePasskey(pk)"
            >
              <template #icon><Trash2 :size="13" /></template>
            </Button>
          </div>
          <Button variant="outline" size="md" class="mt-2" :disabled="registering" @click="openRegisterPasskey">
            <template v-if="registering" #icon><Loader2 :size="14" class="animate-spin" /></template>
            <template v-else #icon><Fingerprint :size="14" /></template>
            {{ registering ? t('settings.auth.passkeyTouch') : t('settings.auth.passkeyAdd') }}
          </Button>
        </div>
      </template>
    </Card>

    <!-- Change password modal -->
    <Modal v-model="showChangePassword" :title="t('settings.auth.changePassword')" size="md">
      <form class="space-y-4" @submit.prevent="submitPassword">
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('settings.oldPassword') }}</label>
          <Input v-model="cpForm.old" type="password" autocomplete="current-password" :disabled="cpMutation.isPending.value" />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('settings.newPassword') }}</label>
          <Input v-model="cpForm.new" type="password" autocomplete="new-password" :invalid="cpTooShort" :disabled="cpMutation.isPending.value" />
          <p v-if="cpTooShort" class="mt-1 text-xs text-danger">
            {{ t('settings.auth.passwordTooShort') }}
          </p>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('settings.confirmPassword') }}</label>
          <Input v-model="cpForm.confirm" type="password" autocomplete="new-password" :invalid="cpMismatch" :disabled="cpMutation.isPending.value" />
          <p v-if="cpMismatch" class="mt-1 text-xs text-danger">
            {{ t('settings.auth.passwordMismatch') }}
          </p>
        </div>
      </form>
      <template #footer>
        <Button variant="ghost" size="sm" :disabled="cpMutation.isPending.value" @click="showChangePassword = false">
          {{ t('common.cancel') }}
        </Button>
        <Button variant="primary" size="sm" :loading="cpMutation.isPending.value" :disabled="!cpCanSubmit" @click="submitPassword">
          {{ t('common.save') }}
        </Button>
      </template>
    </Modal>

    <!-- Initial setup modal -->
    <Modal v-model="showSetup" :title="t('settings.auth.setupAction')" size="md">
      <form class="space-y-4" @submit.prevent="submitSetup">
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('auth.username') }}</label>
          <Input v-model="setupForm.username" type="text" autocomplete="username" :disabled="setupMutation.isPending.value" />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('auth.password') }}</label>
          <Input v-model="setupForm.password" type="password" autocomplete="new-password" :invalid="setupTooShort" :disabled="setupMutation.isPending.value" />
          <p v-if="setupTooShort" class="mt-1 text-xs text-danger">
            {{ t('settings.auth.passwordTooShort') }}
          </p>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('settings.confirmPassword') }}</label>
          <Input v-model="setupForm.confirm" type="password" autocomplete="new-password" :invalid="setupMismatch" :disabled="setupMutation.isPending.value" />
          <p v-if="setupMismatch" class="mt-1 text-xs text-danger">
            {{ t('settings.auth.passwordMismatch') }}
          </p>
        </div>
      </form>
      <template #footer>
        <Button variant="ghost" size="sm" :disabled="setupMutation.isPending.value" @click="showSetup = false">
          {{ t('common.cancel') }}
        </Button>
        <Button variant="primary" size="sm" :loading="setupMutation.isPending.value" :disabled="!setupCanSubmit" @click="submitSetup">
          {{ t('common.create') }}
        </Button>
      </template>
    </Modal>

    <!-- Register passkey modal -->
    <Modal v-model="showRegisterPasskey" :title="t('settings.auth.passkeyAdd')" size="sm">
      <div class="space-y-4">
        <div>
          <label class="mb-1 block text-xs font-medium text-fg-muted">{{ t('settings.auth.passkeyLabel') }}</label>
          <Input
            v-model="passkeyLabel"
            type="text"
            :placeholder="t('settings.auth.passkeyLabelPlaceholder')"
            :disabled="registering"
          />
          <p class="mt-1 text-xs text-fg-subtle">{{ t('settings.auth.passkeyLabelHint') }}</p>
        </div>
        <div class="rounded-md border border-info/30 bg-info/10 p-3 text-xs text-info">
          <Fingerprint :size="12" class="-mt-0.5 mr-1 inline align-middle" />
          {{ t('settings.auth.passkeyRegisterHint') }}
        </div>
      </div>
      <template #footer>
        <Button variant="ghost" size="sm" :disabled="registering" @click="showRegisterPasskey = false">
          {{ t('common.cancel') }}
        </Button>
        <Button variant="primary" size="sm" :loading="registering" :disabled="!passkeySupport.available" @click="registerPasskey">
          <template v-if="registering" #icon><Loader2 :size="14" class="animate-spin" /></template>
          <template v-else #icon><Fingerprint :size="14" /></template>
          {{ registering ? t('settings.auth.passkeyTouch') : t('settings.auth.passkeyAdd') }}
        </Button>
      </template>
    </Modal>

    <!-- Logout confirmation -->
    <Modal v-model="showLogoutConfirm" :title="t('settings.auth.confirmLogoutTitle')" size="sm">
      <p class="text-sm text-fg-muted">{{ t('settings.auth.confirmLogout') }}</p>
      <template #footer>
        <Button variant="ghost" size="sm" @click="showLogoutConfirm = false">
          {{ t('common.cancel') }}
        </Button>
        <Button variant="danger" size="sm" @click="doLogout">
          <template #icon><LogOut :size="14" /></template>
          {{ t('auth.logout') }}
        </Button>
      </template>
    </Modal>
    <!-- v3.13.92: 圆形头像裁剪器 -->
    <AvatarCropper
      :open="pendingAvatar !== null"
      :file="pendingAvatar"
      @confirm="onCropConfirm"
      @cancel="pendingAvatar = null"
    />
  </div>
</template>
